import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DECODE_CONCURRENCY,
  MAX_CACHED_FRAMES,
  PREFETCH_AHEAD,
  PREFETCH_BEHIND,
  PREVIEW_CACHE_BYTES,
} from './constants'
import type { DocPoint } from './features'
import { FrameLoader } from './frameLoader'
import { frameFromResult, type Frame } from './messages'
import { decodeStageImage, frameWindow, orderByOffset, windowRank } from './playback'
import { readPhotoBlob } from './readPhoto'
import { useLatest, useLatestRequest } from './useLatest'

export type StageFrame = Frame & { featureIndex: number | null }

const EMPTY_FRAME: StageFrame = { image: null, message: null, featureIndex: null }

export const useFrameNavigator = (file: Blob, points: DocPoint[], visible: number[]) => {
  const { t } = useTranslation('documentation')
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [cursor, setCursor] = useState(0)
  const [frame, setFrame] = useState<StageFrame>(EMPTY_FRAME)
  const [loader, setLoader] = useState<FrameLoader | null>(null)
  const cursorRef = useLatest(cursor)
  const visibleRef = useLatest(visible)
  const request = useLatestRequest()

  useEffect(() => {
    const next = new FrameLoader({
      file,
      points,
      read: readPhotoBlob,
      decode: blob => decodeStageImage(blob, stageRef.current),
      rank: featureIndex => windowRank(visibleRef.current, cursorRef.current, featureIndex),
      concurrency: DECODE_CONCURRENCY,
      budget: PREVIEW_CACHE_BYTES,
      maxFrames: MAX_CACHED_FRAMES,
    })
    setLoader(next)
    return () => {
      request.cancel()
      next.dispose()
    }
  }, [cursorRef, file, points, request, visibleRef])

  const select = useCallback(
    async (position: number) => {
      const items = visibleRef.current
      if (!loader || !items.length) return
      const next = Math.max(0, Math.min(items.length - 1, position))
      cursorRef.current = next
      setCursor(next)
      const isCurrent = request.start()
      loader.prune()
      const result = frameFromResult(t, await loader.load(items[next], true))
      if (isCurrent() && result) setFrame({ ...result, featureIndex: items[next] })
    },
    [cursorRef, loader, request, t, visibleRef]
  )

  const selectRef = useLatest(select)

  useEffect(() => {
    if (loader) void selectRef.current(0)
  }, [loader, selectRef, visible])

  useEffect(() => {
    if (!loader || !visible.length) return
    loader.prune()
    const positions = frameWindow(cursor, visible.length, PREFETCH_AHEAD, PREFETCH_BEHIND).slice(1)
    orderByOffset(positions.map(position => ({ position, offset: points[visible[position]]?.image?.offset ?? 0 })))
      .map(item => visible[item.position])
      .forEach(featureIndex => void loader.load(featureIndex))
  }, [cursor, loader, points, visible])

  return { cursor, cursorRef, frame, select, stageRef }
}
